import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // DODANO
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const InsertLine = ({ 
  isOpen, 
  onClose, 
  tableName, 
  columns, 
  columnSettings, 
  dropdownOptions, 
  newRow, 
  setNewRow, 
  handleInsert 
}) => {
  const { t } = useTranslation(); // HOOK ZA PREVOD
  const [errors, setErrors] = useState({});
  const [realDbTypes, setRealDbTypes] = useState({});
  const [fetchingTypes, setFetchingTypes] = useState(false);

  useEffect(() => {
    if (isOpen && tableName) {
      const fetchRealTypes = async () => {
        setFetchingTypes(true);
        try {
          const { data, error } = await supabase.rpc('get_table_column_types', { 
            t_name: tableName 
          });
          if (!error && data) {
            const typesObj = data.reduce((acc, curr) => {
              acc[curr.column_name] = curr.data_type;
              return acc;
            }, {});
            setRealDbTypes(typesObj);
          }
        } catch (err) {
          console.error("Errore nel recupero dei tipi:", err);
        } finally {
          setFetchingTypes(false);
        }
      };
      fetchRealTypes();
    }
  }, [isOpen, tableName]);

  if (!isOpen) return null;

  const getInputLogic = (col) => {
    const dbType = realDbTypes[col] || '';
    const isNumber = dbType.includes('numeric') || dbType.includes('int') || dbType.includes('double') || dbType.includes('real');
    const isDate = dbType.includes('date') || dbType.includes('timestamp');
    const isBool = dbType.includes('bool');

    if (isDate) return { type: 'date', label: '[ DATE ]' };
    if (isNumber) return { type: 'number', label: '[ NUMERIC ]' };
    if (isBool) return { type: 'boolean', label: '[ BOOLEAN ]' };
    return { type: 'text', label: '[ TEXT ]' };
  };

  const handleInputChange = (col, value) => {
    if (errors[col]) setErrors({ ...errors, [col]: null });
    setNewRow({ ...newRow, [col]: value });
  };

  const validateAndPrepare = () => {
    const newErrors = {};
    const preparedRow = { ...newRow };

    columns.forEach(col => {
      const value = preparedRow[col];
      const logic = getInputLogic(col);

      if (logic.type === 'date' && value) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) newErrors[col] = t('insert_line.validation.invalid_date');
      }
      if (logic.type === 'number' && value) {
        if (isNaN(value) || value.toString().trim() === "") {
          newErrors[col] = t('insert_line.validation.invalid_number');
        } else {
          preparedRow[col] = Number(value);
        }
      }
      if (logic.type === 'boolean') {
        preparedRow[col] = value === true || value === 'true';
      }
      if (value === '' || value === undefined) preparedRow[col] = null;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? preparedRow : null;
  };

  const onConfirm = () => {
    const validatedData = validateAndPrepare();
    if (validatedData) handleInsert(validatedData);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-background/90 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="bg-card w-full max-w-3xl rounded-[3rem] border border-border shadow-2xl overflow-hidden animate-in zoom-in-95">
        
        {/* Header */}
        <div className="p-10 border-b border-border/50 flex justify-between items-center bg-muted/20">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tighter text-foreground">
              {t('insert_line.header.title')}
            </h2>
            <p className="text-[10px] text-primary font-black uppercase tracking-widest mt-1">
              {fetchingTypes 
                ? t('insert_line.header.loading_structure') 
                : `${t('insert_line.header.table_label')}${tableName?.replace('inv_', '').replace(/_/g, ' ')}`}
            </p>
          </div>
          <button onClick={onClose} className="p-4 hover:bg-muted rounded-2xl transition-all"><Icon name="X" size={24} /></button>
        </div>
        
        {/* Body */}
        <div className="p-10 max-h-[60vh] overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {columns.map(col => {
              const logic = getInputLogic(col);
              const isDropdown = columnSettings[col]?.type === 'dropdown';

              return (
                <div key={col} className="space-y-2">
                  <div className="flex justify-between items-end px-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{col.replace(/_/g, ' ')}</label>
                    <span className="text-[8px] font-black text-primary/60 bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">{logic.label}</span>
                  </div>
                  
                  <div className="relative group">
                    {logic.type === 'boolean' ? (
                      <button 
                        type="button"
                        onClick={() => handleInputChange(col, !newRow[col])}
                        className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl border-2 transition-all ${newRow[col] ? 'bg-primary/10 border-primary text-primary' : 'bg-muted/20 border-border/60 text-muted-foreground'}`}
                      >
                        <span className="text-xs font-black uppercase tracking-widest">
                          {newRow[col] ? t('insert_line.inputs.true') : t('insert_line.inputs.false')}
                        </span>
                        <div className={`w-10 h-6 rounded-full p-1 transition-colors ${newRow[col] ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${newRow[col] ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                      </button>
                    ) : (
                      <div className="relative">
                        <input 
                          list={isDropdown ? `list-${col}` : undefined} // POVEZIVANJE DATALISTA
                          type={logic.type === 'date' ? 'date' : 'text'}
                          inputMode={logic.type === 'number' ? 'decimal' : 'text'}
                          className={`w-full bg-background border ${errors[col] ? 'border-destructive' : 'border-border/60'} rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-4 ${errors[col] ? 'focus:ring-destructive/10' : 'focus:ring-primary/10'} transition-all`}
                          placeholder={isDropdown ? t('insert_line.inputs.dropdown_placeholder') : ""}
                          value={newRow[col] || ''}
                          onChange={(e) => handleInputChange(col, e.target.value)}
                          onClick={(e) => logic.type === 'date' && e.target.showPicker && e.target.showPicker()}
                        />
                        
                        {/* AKO JE DROPDOWN - DODAJ DATALIST I IKONU */}
                        {isDropdown && (
                          <>
                            <datalist id={`list-${col}`}>
                              {(dropdownOptions[col] || []).map((opt, i) => (
                                <option key={i} value={opt} />
                              ))}
                            </datalist>
                            <div className="absolute right-5 top-1/2 -translate-y-1/2 text-primary pointer-events-none opacity-40">
                              <Icon name="ChevronDown" size={18} />
                            </div>
                          </>
                        )}

                        {/* IKONA ZA KALENDAR AKO NIJE DROPDOWN, A JESTE DATE */}
                        {!isDropdown && logic.type === 'date' && (
                          <div className="absolute right-5 top-1/2 -translate-y-1/2 text-primary pointer-events-none opacity-40">
                            <Icon name="Calendar" size={18} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors[col] && <p className="text-[9px] font-black text-destructive uppercase tracking-widest ml-1">{errors[col]}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-10 bg-muted/10 border-t border-border/50 flex gap-4">
          <button onClick={onClose} className="flex-1 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-muted transition-all">
            {t('insert_line.footer.cancel')}
          </button>
          <button onClick={onConfirm} className="flex-[2] py-5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/30 hover:scale-[1.02] transition-all">
            {t('insert_line.footer.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertLine;